import {
    Button,
    Card,
    Col,
    DatePicker,
    Empty,
    Layout,
    message,
    Popconfirm,
    Row,
    Select,
    Space,
    Spin,
    Table,
    Tag,
    Typography,
} from 'antd';
import {
    CalendarOutlined,
    ClockCircleOutlined,
    DeleteOutlined,
    PlusOutlined,
    SaveOutlined,
    WarningOutlined,
} from '@ant-design/icons';
import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import apis from '../../apis';
import { competitionList } from '../../constant/RegisterPageConst';

const { Content } = Layout;
const { Title, Text } = Typography;

/**
 * Returns a human-readable status for a given deadline string.
 */
const getDeadlineStatus = (deadlineStr) => {
    if (!deadlineStr) return { label: 'No deadline', color: 'default' };
    const now = new Date();
    const deadline = new Date(deadlineStr);
    const diffMs = deadline - now;

    if (diffMs <= 0) return { label: 'Expired', color: 'red' };
    if (diffMs <= 24 * 60 * 60 * 1000) return { label: 'Urgent (<24h)', color: 'orange' };
    if (diffMs <= 3 * 24 * 60 * 60 * 1000) return { label: 'Soon (<3 days)', color: 'gold' };
    return { label: 'Active', color: 'green' };
};

/**
 * JuryDeadlineSettings — Admin panel to manage per-category jury scoring deadlines.
 *
 * Reads/writes `juryDeadlines` on the `systemSettings/global` Firestore document.
 * Keys = competition category names (e.g. "Piano Solo", "Harp").
 * Values = ISO 8601 date-time strings.
 */
const JuryDeadlineSettings = () => {
    const [deadlines, setDeadlines] = useState({}); // { "Piano Solo": "2026-06-15T23:59:00+07:00", ... }
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    // New-row form state
    const [newCategory, setNewCategory] = useState(null);
    const [newDate, setNewDate] = useState(null);

    // ------- Fetch on mount -------
    useEffect(() => {
        fetchDeadlines();
    }, []);

    const fetchDeadlines = async () => {
        setLoading(true);
        try {
            const res = await apis.systemSettings.getGlobalSettings();
            if (res.data?.juryDeadlines) {
                setDeadlines(res.data.juryDeadlines);
            }
        } catch (err) {
            console.error('Error fetching jury deadlines:', err);
            message.error('Failed to load jury deadlines.');
        } finally {
            setLoading(false);
        }
    };

    // ------- Save to Firestore -------
    const handleSave = async () => {
        setSaving(true);
        try {
            const res = await apis.systemSettings.updateGlobalSettings({ juryDeadlines: deadlines });
            if (res.error) throw new Error(res.error);
            message.success('Jury deadlines saved successfully.');
        } catch (err) {
            console.error('Error saving jury deadlines:', err);
            message.error('Failed to save jury deadlines.');
        } finally {
            setSaving(false);
        }
    };

    /**
     * Set a dayjs date to end-of-day 23:59:59.999 and return ISO string.
     */
    const toEndOfDay = (date) => {
        return date.hour(23).minute(59).second(59).millisecond(999).toISOString();
    };

    // ------- Add a new category deadline -------
    const handleAdd = () => {
        if (!newCategory) {
            message.warning('Please select a competition category.');
            return;
        }
        if (!newDate) {
            message.warning('Please pick a deadline date.');
            return;
        }
        if (deadlines[newCategory]) {
            message.warning(`A deadline for "${newCategory}" already exists. Remove it first or pick a different category.`);
            return;
        }

        setDeadlines((prev) => ({
            ...prev,
            [newCategory]: toEndOfDay(newDate),
        }));
        setNewCategory(null);
        setNewDate(null);
        message.info(`Added deadline for ${newCategory} (end of ${newDate.format('YYYY-MM-DD')}). Remember to click Save.`);
    };

    // ------- Remove a category deadline -------
    const handleRemove = (category) => {
        setDeadlines((prev) => {
            const copy = { ...prev };
            delete copy[category];
            return copy;
        });
        message.info(`Removed deadline for ${category}. Remember to click Save.`);
    };

    // ------- Update an existing deadline date -------
    const handleDateChange = (category, date) => {
        if (!date) return;
        setDeadlines((prev) => ({
            ...prev,
            [category]: toEndOfDay(date),
        }));
    };

    // ------- Build category options (exclude already-set categories) -------
    const usedCategories = new Set(Object.keys(deadlines));
    const categoryOptions = Object.values(competitionList)
        .filter((cat) => !usedCategories.has(cat))
        .map((cat) => ({ label: cat, value: cat }));

    // ------- Table data -------
    const tableData = Object.entries(deadlines)
        .sort(([, a], [, b]) => new Date(a) - new Date(b))
        .map(([category, deadline]) => ({
            key: category,
            category,
            deadline,
        }));

    const columns = [
        {
            title: 'Category',
            dataIndex: 'category',
            key: 'category',
            render: (text) => <Text strong>{text}</Text>,
        },
        {
            title: 'Deadline (end of day)',
            dataIndex: 'deadline',
            key: 'deadline',
            render: (deadline, record) => (
                <DatePicker
                    format="YYYY-MM-DD"
                    value={dayjs(deadline)}
                    onChange={(date) => handleDateChange(record.category, date)}
                    style={{ width: 180 }}
                    allowClear={false}
                />
            ),
        },
        {
            title: 'Status',
            key: 'status',
            render: (_, record) => {
                const status = getDeadlineStatus(record.deadline);
                return <Tag color={status.color}>{status.label}</Tag>;
            },
        },
        {
            title: 'Time Remaining',
            key: 'remaining',
            render: (_, record) => {
                const deadline = new Date(record.deadline);
                const now = new Date();
                const diff = deadline - now;

                if (diff <= 0) return <Text type="danger">Expired</Text>;

                const days = Math.floor(diff / (1000 * 60 * 60 * 24));
                const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
                const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

                if (days > 0) return <Text>{days}d {hours}h</Text>;
                if (hours > 0) return <Text type="warning">{hours}h {minutes}m</Text>;
                return <Text type="danger">{minutes}m</Text>;
            },
        },
        {
            title: 'Action',
            key: 'action',
            width: 80,
            render: (_, record) => (
                <Popconfirm
                    title={`Remove deadline for "${record.category}"?`}
                    description="Jury members for this category will have unrestricted access."
                    onConfirm={() => handleRemove(record.category)}
                    okText="Remove"
                    okButtonProps={{ danger: true }}
                >
                    <Button type="text" danger icon={<DeleteOutlined />} size="small" />
                </Popconfirm>
            ),
        },
    ];

    return (
        <Content style={{ padding: '24px 50px', minHeight: 280 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                <div>
                    <Title level={2} style={{ marginBottom: 4 }}>
                        <ClockCircleOutlined style={{ marginRight: 10 }} />
                        Jury Deadline Settings
                    </Title>
                    <Text type="secondary">
                        Set per-category scoring deadlines for jury members. After a deadline passes, jury members for that category are blocked from submitting scores.
                    </Text>
                </div>
                <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    size="large"
                    loading={saving}
                    onClick={handleSave}
                    disabled={loading}
                >
                    Save All Changes
                </Button>
            </div>

            {loading ? (
                <div style={{ textAlign: 'center', padding: 80 }}>
                    <Spin size="large" />
                </div>
            ) : (
                <>
                    {/* Add new deadline */}
                    <Card
                        size="small"
                        title={<><PlusOutlined /> Add Category Deadline</>}
                        style={{ marginBottom: 24, marginTop: 16 }}
                    >
                        <Row gutter={12} align="middle">
                            <Col flex="auto">
                                <Select
                                    placeholder="Select competition category"
                                    value={newCategory}
                                    onChange={setNewCategory}
                                    options={categoryOptions}
                                    style={{ width: '100%' }}
                                    showSearch
                                    allowClear
                                    notFoundContent={
                                        <Empty
                                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                                            description="All categories have deadlines set"
                                        />
                                    }
                                />
                            </Col>
                            <Col>
                                <DatePicker
                                    format="YYYY-MM-DD"
                                    value={newDate}
                                    onChange={setNewDate}
                                    placeholder="Select deadline date"
                                    style={{ width: 180 }}
                                />
                            </Col>
                            <Col>
                                <Button
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    onClick={handleAdd}
                                >
                                    Add
                                </Button>
                            </Col>
                        </Row>
                    </Card>

                    {/* Current deadlines table */}
                    <Card
                        title={<><CalendarOutlined /> Current Deadlines ({tableData.length})</>}
                    >
                        {tableData.length === 0 ? (
                            <Empty description="No jury deadlines configured. Jury can score at any time." />
                        ) : (
                            <>
                                <Space style={{ marginBottom: 16 }}>
                                    <Tag icon={<WarningOutlined />} color="orange">
                                        Unsaved changes are not persisted — always click "Save All Changes"
                                    </Tag>
                                </Space>
                                <Table
                                    columns={columns}
                                    dataSource={tableData}
                                    pagination={false}
                                    size="middle"
                                    bordered
                                />
                            </>
                        )}
                    </Card>
                </>
            )}
        </Content>
    );
};

export default JuryDeadlineSettings;
